import {
  OPEN_WORLD_ENTRY_ROUTE,
  OPEN_WORLD_ENTRY_VERSION,
  OPEN_WORLD_MAP_ROUTE,
  OPEN_WORLD_PARTY_ACTION_ROUTE,
  OPEN_WORLD_PARTY_STATE_ROUTE,
  OPEN_WORLD_PARTY_VERSION,
  OPEN_WORLD_SOCKET_ROUTE,
  OPEN_WORLD_STATE_ROUTE,
  OPEN_WORLD_TICKET_ROUTE,
  type OpenWorldCoordinate,
  type OpenWorldEntryResult,
  type OpenWorldEntrySide,
  type OpenWorldEntryState,
  type OpenWorldMapView,
  type OpenWorldPartyAction,
  type OpenWorldPartyResponse,
  type OpenWorldPartyView,
  type OpenWorldTicketResult,
  openWorldEntryResultSchema,
  openWorldEntryStateSchema,
  openWorldMapViewSchema,
  openWorldPartyResponseSchema,
  openWorldPartyViewSchema,
  openWorldTicketResultSchema,
} from '@kenocyber/shared';
import { API_BASE_URL } from '../auth/client';

/**
 * **As três rotas do mundo aberto, e a URL do seu socket.**
 *
 * ## Porque o bilhete é pedido por HTTP
 *
 * Um handshake de WebSocket não está coberto por CORS e leva cookies por
 * `SameSite` — e este cliente e esta API estão em sítios diferentes em todos os
 * ambientes que existem. Portanto o browser prova quem é da forma ordinária,
 * um cookie num POST cross-origin, e recebe uma capacidade curta para pôr na
 * query do socket.
 *
 * O bilhete é de **uso único e vive trinta segundos**. Cada ligação e cada
 * reconexão pede um novo; um cliente que guardasse um e o apresentasse duas
 * vezes seria recusado pela sala, que é exatamente o objetivo dele.
 *
 * ## O que este módulo deliberadamente não faz
 *
 * Não repete, não põe em fila e não decide o que uma falha significa. Entrar
 * numa expedição é uma coisa que uma pessoa escolheu fazer; um cliente que
 * tentasse sozinho outra vez seria um cliente a jogar por ela.
 */

/** Uma recusa de uma rota do mundo aberto. Uma razão estável, nunca uma frase. */
export class OpenWorldApiError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = 'OpenWorldApiError';
  }
}

async function post(route: string, body: unknown): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}${route}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new OpenWorldApiError(response.status === 401 ? 'unauthorized' : 'request_failed');
  }
  return response.json();
}

/** Onde esta conta está. Uma leitura, e nunca uma entrada. */
export async function fetchOpenWorldState(): Promise<OpenWorldEntryState> {
  const response = await fetch(`${API_BASE_URL}${OPEN_WORLD_STATE_ROUTE}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new OpenWorldApiError(response.status === 401 ? 'unauthorized' : 'request_failed');
  }
  const parsed = openWorldEntryStateSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new OpenWorldApiError('bad_response');
  }
  return parsed.data;
}

/**
 * **Entrar por um lado, ou retomar o que já está a decorrer.**
 *
 * O lado é a única coisa que este cliente diz. O porto, a coordenada, o casco,
 * a capacidade e o bilhete vêm todos de volta — nenhum deles é proposto.
 */
export async function enterOpenWorld(side: OpenWorldEntrySide): Promise<OpenWorldEntryResult> {
  const parsed = openWorldEntryResultSchema.safeParse(
    await post(OPEN_WORLD_ENTRY_ROUTE, { v: OPEN_WORLD_ENTRY_VERSION, side }),
  );
  if (!parsed.success) {
    throw new OpenWorldApiError('bad_response');
  }
  return parsed.data;
}

/**
 * Um bilhete para um quadrante. Pedido por uma reconexão e por uma travessia.
 *
 * O quadrante é **verificado** contra onde a autoridade diz que a conta está.
 * Pedir um bilhete para o vizinho antes de a travessia se comprometer ouve
 * `wrong_quadrant`, e é isso que impede um bilhete de ser um teletransporte.
 */
export async function fetchOpenWorldTicket(
  quadrant: OpenWorldCoordinate,
): Promise<OpenWorldTicketResult> {
  const parsed = openWorldTicketResultSchema.safeParse(
    await post(OPEN_WORLD_TICKET_ROUTE, { v: OPEN_WORLD_ENTRY_VERSION, quadrant }),
  );
  if (!parsed.success) {
    throw new OpenWorldApiError('bad_response');
  }
  return parsed.data;
}

/**
 * A URL do socket, com o bilhete na query.
 *
 * Na query e não num cabeçalho porque a API de WebSocket do browser não deixa
 * pôr cabeçalhos num handshake. O bilhete é curto, de uso único e gasto no
 * instante em que chega, que é o que torna a exposição aceitável.
 */
export function openWorldSocketUrl(ticket: string): string {
  const base = new URL(`${API_BASE_URL}${OPEN_WORLD_SOCKET_ROUTE}`);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.searchParams.set('ticket', ticket);
  return base.toString();
}

/**
 * **O mapa desta conta.** Uma leitura sem corpo, e sem parâmetros.
 *
 * Sem corpo é a propriedade: não há campo para uma conta, para um mundo, para
 * uma coordenada ou para uma lista de descobertas, portanto este cliente não
 * tem como pedir o mapa de outra pessoa nem afirmar que esteve algures.
 *
 * O que volta é a grelha conhecida — limites, portos, onde se está, onde já se
 * esteve, e os aliados da expedição ativa. **Nada do conteúdo dos quadrantes**,
 * e nada que este ficheiro pudesse esconder mais tarde: o que não chega não
 * precisa de ser escondido.
 */
export async function fetchOpenWorldMap(): Promise<OpenWorldMapView> {
  const response = await fetch(`${API_BASE_URL}${OPEN_WORLD_MAP_ROUTE}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new OpenWorldApiError(response.status === 401 ? 'unauthorized' : 'request_failed');
  }
  const parsed = openWorldMapViewSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new OpenWorldApiError('bad_response');
  }
  return parsed.data;
}

/**
 * **O lobby desta conta.** Uma leitura, e nunca uma decisão.
 *
 * O que ela traz depende de quem pergunta: quem não está em PT nenhuma vê os
 * convites que recebeu, e quem está vê a lista, o lado e a prontidão. A conta
 * vem do cookie, e não há campo nenhum onde este cliente a pudesse dizer.
 */
export async function fetchOpenWorldParty(): Promise<OpenWorldPartyView> {
  const response = await fetch(`${API_BASE_URL}${OPEN_WORLD_PARTY_STATE_ROUTE}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new OpenWorldApiError(response.status === 401 ? 'unauthorized' : 'request_failed');
  }
  const parsed = openWorldPartyViewSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new OpenWorldApiError('bad_response');
  }
  return parsed.data;
}

/**
 * **Uma das nove ações**, e todas pela mesma porta.
 *
 * A resposta é sempre o lobby inteiro, como quem pediu o vê — nunca um diff.
 * Um cliente que aplicasse diferenças teria de as saber ordenar, que é uma
 * segunda máquina de estados a viver no navegador.
 *
 * O bilhete só vem quando a ação foi um início que se comprometeu.
 */
export async function actOnOpenWorldParty(
  action: OpenWorldPartyAction,
): Promise<OpenWorldPartyResponse> {
  const parsed = openWorldPartyResponseSchema.safeParse(
    await post(OPEN_WORLD_PARTY_ACTION_ROUTE, { v: OPEN_WORLD_PARTY_VERSION, action }),
  );
  if (!parsed.success) {
    throw new OpenWorldApiError('bad_response');
  }
  return parsed.data;
}
